import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, AlertCircle, XCircle } from 'lucide-react';
import { useMyOrders } from '../hooks/useFarmOrder';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatBRL, formatDate } from '@/lib/format';

const STATUS_LABELS: Record<string, string> = {
  quote:     'Orçamento',
  reserved:  'Reservado',
  printing:  'Imprimindo',
  done:      'Concluído',
  cancelled: 'Cancelado',
  refunded:  'Reembolsado',
};
const STATUS_CHIPS: Record<string, string> = {
  quote:     'chip-neutral',
  reserved:  'chip-info',
  printing:  'chip-warning',
  done:      'chip-positive',
  cancelled: 'chip-negative',
  refunded:  'chip-neutral',
};

export function MyOrders() {
  const { orders, loading, error, fetchOrders, cancelOrder } = useMyOrders();
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { void fetchOrders(); }, [fetchOrders]);

  async function handleCancel(e: React.MouseEvent, orderId: string) {
    e.preventDefault(); // não navegar para o detalhe ao clicar em cancelar
    setBusy(orderId);
    try {
      await cancelOrder(orderId);
    } finally {
      setBusy(null);
    }
  }

  return (
    <PageShell title="Meus pedidos" subtitle="Pedidos realizados em farms." back="/farms">
      {loading ? (
        <div className="space-y-3">{[1,2,3].map((i) => <div key={i} className="h-20 rounded-2xl bg-surface-subtle animate-pulse" />)}</div>
      ) : error ? (
        <div className="card p-6 flex items-start gap-3 text-negative">
          <AlertCircle className="shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-medium text-sm">Erro ao carregar pedidos</p>
            <p className="text-xs mt-1 text-ink-muted">{error}</p>
            <button onClick={() => void fetchOrders()} className="text-xs underline mt-2">Tentar novamente</button>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="card p-8">
          <EmptyState icon={ShoppingBag} title="Nenhum pedido"
            description="Acesse o marketplace para encontrar farms e reservar máquinas."
            action={<Link to="/farms/marketplace" className="btn-primary">Ver marketplace</Link>} />
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <Link key={o.id} to={`/farms/pedidos/${o.id}`} className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink text-sm truncate">{o.farms?.name ?? 'Farm'}</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  <span className="font-mono text-ink-soft">{o.id.slice(0,8)}…</span>
                  {' · '}{formatDate(o.created_at)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-ink tabular-nums">{formatBRL(o.total_amount_cents)}</p>
                <span className={STATUS_CHIPS[o.status] ?? 'chip-neutral'}>{STATUS_LABELS[o.status] ?? o.status}</span>
              </div>
              {(o.status === 'quote' || (o.status === 'reserved' && o.payment_status === 'pending')) && (
                <button
                  onClick={(e) => void handleCancel(e, o.id)}
                  disabled={busy === o.id}
                  className="btn-secondary !py-1.5 !px-2.5 !text-xs gap-1 text-negative border-negative/30 shrink-0"
                  title="Cancelar pedido (antes do pagamento)"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  {busy === o.id ? '…' : 'Cancelar'}
                </button>
              )}
            </Link>
          ))}
        </div>
      )}
    </PageShell>
  );
}
